import React, { useState, useEffect } from "react";
import CheckboxTree from "react-checkbox-tree";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import { get, post } from "../../services/api"; // Importing `get` and `post` methods
import FullScreenLoader from "../../components/FullScreenLoader";

const FileManagerSetup = () => {
  const token = localStorage.getItem("token");
  const [treeData, setTreeData] = useState([]);
  const [checked, setChecked] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const organizationId = localStorage.getItem("organizationId");
  const roleId = localStorage.getItem("roleId");

  // Modal States for Create, Edit, and Delete
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const [newNodeLabel, setNewNodeLabel] = useState("");
  const [currentNode, setCurrentNode] = useState(null);

  // Fetch tree data from the API
  // Fetch tree data function
  const fetchTreeData = async () => {
    setLoading(true);
    try {
      const response = await get(
        `FileManager/GetAllFileManagerSetup?organizationId=${organizationId}&roleId=${roleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTreeData(response.menues);
    } catch (error) {
      console.error("Error fetching tree data:", error);
    } finally {
      setLoading(false);
    }
  }; // Initial load of tree data
  useEffect(() => {
    fetchTreeData();
  }, []);

  // Handle Create Node
  const handleCreateNode = async () => {
    const newNode = {
      name: newNodeLabel,
      createdBy: localStorage.getItem("userId"),
      prntMenuId: currentNode.id,
      organizationId: organizationId,
    };

    try {
      const response = await post("FileManager/CreateNode", newNode, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        fetchTreeData();
        setNewNodeLabel("");
        setIsCreateModalVisible(false); // Close modal after creating node
      } else {
        alert("Error creating node");
      }
    } catch (error) {
      console.error("Error creating node:", error);
    }
  };

  // Handle Edit Node
  const handleEditNode = async () => {
    if (currentNode && currentNode.name) {
      const updatedNode = {
        ...currentNode,
        name: currentNode.name,
        id: currentNode.id,
        prntMenuId: currentNode.prntMenuId,
        createdBy: localStorage.getItem("userId"),
        organizationId: organizationId,
      };

      try {
        const response = await post("FileManager/EditNode", updatedNode, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.isSuccess) {
          const updatedTreeData = treeData.map((node) =>
            node.value === currentNode.value ? updatedNode : node
          );
          fetchTreeData();
          setIsEditModalVisible(false); // Close modal after editing
        } else {
          alert("Error updating node");
        }
      } catch (error) {
        console.error("Error editing node:", error);
      }
    }
  };

  // Handle Delete Node
  const handleDeleteNode = async () => {
    if (currentNode) {
      try {
        const response = await get(
          `FileManager/DeleteNode?nodeId=${currentNode.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.isSuccess) {
          fetchTreeData();
          setIsDeleteModalVisible(false); // Close modal after deletion
        } else {
          alert("Error deleting node");
        }
      } catch (error) {
        console.error("Error deleting node:", error);
      }
    }
  };

  // Open Modal for Create
  const openCreateModal = (node) => {
    setError("");
    setCurrentNode(node);
    setIsCreateModalVisible(true);
  };

  // Open Modal for Edit
  const openEditModal = (node) => {
    setError("");
    setCurrentNode(node);
    setIsEditModalVisible(true);
  };

  // Open Modal for Delete
  const openDeleteModal = (node) => {
    setCurrentNode(node);
    setIsDeleteModalVisible(true);
  };

  // Close all modals
  const closeModals = () => {
    setIsCreateModalVisible(false);
    setIsEditModalVisible(false);
    setIsDeleteModalVisible(false);
    setCurrentNode(null); // Reset current node
    setError("");
  };

  // Recursive rendering of nodes with action buttons
  const renderNodes = (nodes) => {
    if (!nodes || !Array.isArray(nodes)) return [];

    // Filter out nodes with duplicate IDs within the same level to prevent "Duplicate value" error
    const uniqueNodes = nodes.filter(
      (node, index, self) => index === self.findIndex((n) => n.id === node.id)
    );

    return uniqueNodes.map((node) => ({
      value: node.id,
      label: (
        <span>
          {node.name}
          <span style={{ marginLeft: 10 }}>
            <button
              className="btn p-0 ms-2"
              onClick={() => openCreateModal(node)}
            >
              <i className="fas fa-plus"></i>
            </button>
            <button
              className="btn p-0 ms-2"
              onClick={() => openEditModal(node)}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              className="btn p-0 ms-2"
              onClick={() => openDeleteModal(node)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </span>
        </span>
      ),
      children: node.children ? renderNodes(node.children) : [],
    }));
  };

  const nodes = renderNodes(treeData);

  return (
    <div>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">File Manager Setup</h2>
        <div className="ms-auto">
          {nodes.length > 0 && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={openCreateModal}
            >
              Create Item
            </button>
          )}
        </div>
      </div>

      {nodes.length ? (
        <div className="card">
          <div className="card-body">
            {/* Render Tree */}
            <div className="hidden-checkboxes">
              <CheckboxTree
                nodes={nodes}
                checked={checked}
                expanded={expanded}
                onCheck={(checked) => setChecked(checked)}
                onExpand={(expanded) => setExpanded(expanded)}
                iconsClass="fa5"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="py-5 d-flex flex-column align-items-center justify-content-center">
          <h5 className="mb-3 text-secondary">No data available</h5>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={openCreateModal}
          >
            Create Item
          </button>
        </div>
      )}

      {/* Create Node Modal */}
      {isCreateModalVisible && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: "block" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="mb-0">Create Item</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModals}
                ></button>
              </div>
              <div className="modal-body">
                <label className="form-label">
                  Item Name<span className="text-danger fs-9">*</span>
                </label>
                <input
                  type="text"
                  value={newNodeLabel}
                  onChange={(e) => {
                    setNewNodeLabel(e.target.value);
                    setError("");
                  }}
                  className="form-control"
                  placeholder="Enter item name"
                />
                {error && <small className="text-danger mt-2">{error}</small>}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={closeModals}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    if (!newNodeLabel.trim()) {
                      setError("This field is required.");
                      return;
                    }
                    handleCreateNode();
                  }}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isCreateModalVisible && <div className="modal-backdrop fade show"></div>}

      {/* Edit Node Modal */}
      {isEditModalVisible && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: "block" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="mb-0">Edit Item</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModals}
                ></button>
              </div>
              <div className="modal-body">
                <label className="form-label">
                  Item Name<span className="text-danger fs-9">*</span>
                </label>
                <input
                  type="text"
                  value={currentNode ? currentNode.name : ""}
                  onChange={(e) => {
                    setCurrentNode({ ...currentNode, name: e.target.value });
                    setError("");
                  }}
                  className="form-control"
                  placeholder="Edit item name"
                />
                {error && <small className="text-danger mt-2">{error}</small>}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={closeModals}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    if (!currentNode.name.trim()) {
                      setError("This field is required.");
                      return;
                    }
                    handleEditNode();
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isEditModalVisible && <div className="modal-backdrop fade show"></div>}

      {/* Delete Node Modal */}
      {isDeleteModalVisible && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: "block" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="mb-0">Delete Item</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModals}
                ></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete this item?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={closeModals}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleDeleteNode}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isDeleteModalVisible && <div className="modal-backdrop fade show"></div>}
      {loading && <FullScreenLoader />}
    </div>
  );
};

export default FileManagerSetup;
